'use client';

import { motion } from "framer-motion";
import { Button } from "./ui/button";
import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { useParams } from 'next/navigation';
import { localizeSpelling } from '@/lib/spelling-localization';
import { BackgroundVideo } from './background-video';

interface HeroSectionProps {
  title?: string;
  subtitle?: string;
  cta?: string;
}

export function HeroSection({ 
  title = "Transforming Industries with AI-Powered Solutions",
  subtitle = "We build custom AI solutions that drive innovation and efficiency across various sectors. Our expertise spans from computer vision to natural language processing.",
  cta = "Get Started"
}: HeroSectionProps = {}) {
  const params = useParams();
  const currentLocale = params?.locale as string || 'en';
  
  // Apply spelling localization based on the current locale
  const localizedTitle = localizeSpelling(title, currentLocale);
  const localizedSubtitle = localizeSpelling(subtitle, currentLocale);
  const localizedCta = localizeSpelling(cta, currentLocale);
  


  return (
    <div className="min-h-screen flex flex-col items-center justify-center text-center px-4 py-16 relative overflow-hidden">
      {/* Background Video */}
      <BackgroundVideo src="https://pub-33ea9fef23824733bfd34c2ea86136dc.r2.dev/hero.mp4" />
      
      {/* Dark overlay for better text readability */}
      <div className="absolute top-0 left-0 w-full h-full bg-black/50 z-10"></div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-3xl mx-auto space-y-8 container relative z-20"
      >

        <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold tracking-tight bg-gradient-to-r from-white via-cyan-200 to-blue-200 bg-clip-text text-transparent drop-shadow-2xl leading-tight py-2">
          {localizedTitle}
        </h1>
        
        <p className="text-lg sm:text-xl md:text-2xl text-gray-100 max-w-3xl mx-auto drop-shadow-lg font-medium">
          {localizedSubtitle}
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button 
            size="pill" 
            variant="gradient" 
            className="font-medium"
            asChild
          >
            <Link href={`/${currentLocale}/contact`}>
              {localizedCta} <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
          <Button 
            size="pill" 
            variant="outline" 
            className="font-medium text-white border-white/30 hover:bg-white/10"
            asChild
          >
            <Link href={`/${currentLocale}/services`}>Our Services</Link>
          </Button>
        </div>
      </motion.div>
    </div>
  );
}